import * as React from 'react'
import { CalendarBlank } from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import {
  FilterActions,
  FilterQuickSelect,
  FilterTrigger,
  type FilterTriggerProps,
} from './filter-popover'
import { SegmentedControl } from './segmented-control'
import { RangeCalendar } from './range-calendar'
import { CalendarDate } from '@internationalized/date'
import type { DateValue } from 'react-aria-components'
import { cn } from '../../lib/utils'

/**
 * PeriodPicker — a fiscal date-range filter as a `Popover`, replacing the heavy
 * "Select Date Range" modal. One surface: quick presets, a Quarter / Half / Year
 * segmented control with inline period + year buttons, and a Custom calendar —
 * no mode-then-grid-then-year clicking through a dialog.
 *
 *   <PeriodPicker label="Date range" onApply={setPeriod} />
 *
 * Edits stage in a draft; Apply commits via `onApply`. The trigger summarizes the
 * applied period (`Q2 2026`, `H1 2026`, `2026`, or a custom `M/D/YYYY – M/D/YYYY`).
 */

export type PeriodMode = 'quarter' | 'half' | 'year' | 'custom'

export interface PeriodValue {
  mode: PeriodMode
  /** Fiscal year (quarter / half / year modes). */
  year?: number
  /** 1–4 (quarter mode). */
  quarter?: 1 | 2 | 3 | 4
  /** 1–2 (half mode). */
  half?: 1 | 2
  /** Custom-mode endpoints. */
  start?: Date | null
  end?: Date | null
  /**
   * Display label for the trigger, overriding the computed summary. Set by
   * relative quick-selects ("Last 6 Months") so the trigger reads the preset
   * name rather than a raw date range.
   */
  label?: string
}

/** A quick-select preset (label + the value it applies). */
export interface PeriodQuickSelect {
  label: string
  value: PeriodValue
}

export interface PeriodPickerProps {
  label: React.ReactNode
  /** Initial applied period (uncontrolled). */
  defaultValue?: PeriodValue
  onApply?: (value: PeriodValue) => void
  /** Years offered in the Quarter / Half / Year selectors (newest first). */
  years?: number[]
  /**
   * Override the right-side quick-select list. Defaults to the fiscal presets
   * (This/Last quarter, This/Last year). Pass relative ranges (e.g. "Last 6
   * Months" as a `custom` value with a `label`) for a trends dashboard.
   */
  quickSelects?: PeriodQuickSelect[]
  /**
   * Where the draft reverts to when the active quick-select is toggled off.
   * Defaults to the current fiscal quarter — fine for the default fiscal
   * presets, but a consumer passing relative `quickSelects` (none of which are a
   * quarter) should pass its own neutral here (e.g. the default "Last 6 months")
   * so deselecting lands on a listed option instead of an unrelated quarter.
   */
  neutralDefault?: PeriodValue
  /**
   * Hide the Quarter/Half/Year/Custom mode toggle and show only the range
   * calendar + quick-selects — a simple relative-range picker (the draft stays
   * in `custom` mode). Pair with relative `quickSelects` and a `custom`
   * `neutralDefault` so deselecting a preset lands on a real range.
   */
  showModes?: boolean
  shape?: FilterTriggerProps['shape']
  /** Trigger height — `md` (h-9, default) or `sm` (h-8) for a compact toolbar. */
  size?: FilterTriggerProps['size']
  labelPlacement?: FilterTriggerProps['labelPlacement']
  className?: string
}

// react-aria range value (DateValue exposes year/month/day — no timezone needed).
type CalRange = { start: DateValue; end: DateValue } | null

const MODE_OPTIONS = [
  { value: 'quarter', label: 'Quarter' },
  { value: 'half', label: 'Half' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom' },
]

const fmtDate = (d: Date) =>
  `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`

/** Trigger summary for the applied period (null when incomplete). */
function summarize(v: PeriodValue | null): string | null {
  if (!v) return null
  if (v.label) return v.label
  if (v.mode === 'quarter' && v.quarter && v.year)
    return `Q${v.quarter} ${v.year}`
  if (v.mode === 'half' && v.half && v.year) return `H${v.half} ${v.year}`
  if (v.mode === 'year' && v.year) return `${v.year}`
  if (v.mode === 'custom' && v.start && v.end)
    return `${fmtDate(v.start)} – ${fmtDate(v.end)}`
  return null
}

/** A selectable period / year chip (calendar glyph + label, brand when active). */
function PeriodOption({
  selected,
  onClick,
  children,
}: {
  selected: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
        selected
          ? 'border-fbui-basil-600 bg-fbui-basil-50 text-fbui-basil-700'
          : 'border-transparent bg-fbui-gray-100 text-fbui-gray-700 hover:bg-fbui-gray-200'
      )}
    >
      <CalendarBlank
        weight="regular"
        aria-hidden="true"
        className="size-4 shrink-0"
      />
      {children}
    </button>
  )
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-1.5 mt-[0.75rem] text-xs font-medium text-fbui-gray-500">
    {children}
  </p>
)

export function PeriodPicker({
  label,
  defaultValue,
  onApply,
  years,
  quickSelects,
  neutralDefault,
  showModes = true,
  shape,
  size,
  labelPlacement = 'border',
  className,
}: PeriodPickerProps) {
  const now = new Date()
  const curYear = now.getFullYear()
  const curQuarter = (Math.floor(now.getMonth() / 3) + 1) as 1 | 2 | 3 | 4
  const yearList = years ?? [curYear, curYear - 1, curYear - 2]

  // Range-only variant opens straight into the custom calendar (no mode toggle).
  const defaultDraft: PeriodValue = showModes
    ? { mode: 'quarter', quarter: curQuarter, year: curYear }
    : { mode: 'custom' }

  const [committed, setCommitted] = React.useState<PeriodValue | null>(
    defaultValue ?? null
  )
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<PeriodValue>(
    committed ?? defaultDraft
  )

  // Mirror a custom (start/end) value into the calendar's react-aria range.
  const toCalRange = (v: PeriodValue | null): CalRange =>
    v?.mode === 'custom' && v.start && v.end
      ? {
          start: new CalendarDate(
            v.start.getFullYear(),
            v.start.getMonth() + 1,
            v.start.getDate()
          ),
          end: new CalendarDate(
            v.end.getFullYear(),
            v.end.getMonth() + 1,
            v.end.getDate()
          ),
        }
      : null

  const [customRange, setCustomRange] = React.useState<CalRange>(
    toCalRange(committed)
  )

  const onOpenChange = (next: boolean) => {
    if (next) {
      setDraft(committed ?? defaultDraft)
      // Re-seed the calendar from the committed custom range so reopening shows it.
      if (committed?.mode === 'custom') setCustomRange(toCalRange(committed))
    }
    setOpen(next)
  }

  const setMode = (mode: string) =>
    setDraft((d) => ({
      ...d,
      mode: mode as PeriodMode,
      year: d.year ?? curYear,
      quarter: mode === 'quarter' ? (d.quarter ?? curQuarter) : d.quarter,
      half: mode === 'half' ? (d.half ?? 1) : d.half,
    }))

  // Presets fill the draft (and reflect in the controls below).
  const presets: { label: string; value: PeriodValue }[] = [
    {
      label: 'This quarter',
      value: { mode: 'quarter', quarter: curQuarter, year: curYear },
    },
    {
      label: 'Last quarter',
      value:
        curQuarter === 1
          ? { mode: 'quarter', quarter: 4, year: curYear - 1 }
          : {
              mode: 'quarter',
              quarter: (curQuarter - 1) as 1 | 2 | 3 | 4,
              year: curYear,
            },
    },
    { label: 'This year', value: { mode: 'year', year: curYear } },
    { label: 'Last year', value: { mode: 'year', year: curYear - 1 } },
  ]

  const quickSelectItems = quickSelects ?? presets

  // Does the draft currently match this preset? Relative presets are identified
  // by their `label`; fiscal presets by their mode + period fields.
  const isPresetActive = (v: PeriodValue): boolean => {
    if (v.label) return draft.label === v.label
    if (draft.mode !== v.mode) return false
    if (v.mode === 'quarter') return draft.quarter === v.quarter && draft.year === v.year
    if (v.mode === 'half') return draft.half === v.half && draft.year === v.year
    if (v.mode === 'year') return draft.year === v.year
    return false
  }

  // Toggle a preset: apply it (seeding the calendar for a custom range so it
  // shows highlighted + flows through Apply), or — if it's already the active
  // one — clear it back to the committed value / neutral default.
  const selectPreset = (value: PeriodValue) => {
    if (isPresetActive(value)) {
      // Deselect → the caller's neutral (or this quarter), so clicking the active
      // preset clears it to a sensible value rather than an unrelated quarter
      // when the quick-selects are relative ranges. Seed the calendar range the
      // same way the select branch does — a custom-mode neutral (e.g. "Last 6
      // months") applies its start/end from `customRange`, so nulling it here
      // would apply the label with no dates.
      const fallback = neutralDefault ?? defaultDraft
      setDraft(fallback)
      setCustomRange(fallback.mode === 'custom' ? toCalRange(fallback) : null)
    } else {
      setDraft(value)
      if (value.mode === 'custom') setCustomRange(toCalRange(value))
    }
  }

  const toDate = (d: CalRange) =>
    d
      ? {
          start: new Date(d.start.year, d.start.month - 1, d.start.day),
          end: new Date(d.end.year, d.end.month - 1, d.end.day),
        }
      : null

  const draftComplete =
    draft.mode === 'custom'
      ? !!(customRange && customRange.start && customRange.end)
      : draft.mode === 'year'
        ? !!draft.year
        : draft.mode === 'half'
          ? !!(draft.half && draft.year)
          : !!(draft.quarter && draft.year)

  const apply = () => {
    const value: PeriodValue =
      draft.mode === 'custom'
        ? { mode: 'custom', ...toDate(customRange), label: draft.label }
        : draft
    setCommitted(value)
    onApply?.(value)
    setOpen(false)
  }

  const summary = summarize(committed)

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <FilterTrigger
          label={label}
          summary={summary}
          active={summary != null}
          shape={shape}
          size={size}
          labelPlacement={labelPlacement}
          placeholder="All dates"
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto">
        <div className="flex gap-[1rem]">
          {/* Left: mode + period selection (or the custom calendar). */}
          <div className={draft.mode === 'custom' ? '' : 'w-72'}>
            {showModes && (
              <SegmentedControl
                options={MODE_OPTIONS}
                value={draft.mode}
                onValueChange={setMode}
                className="w-full"
              />
            )}

            {draft.mode === 'quarter' && (
              <>
                <SectionLabel>Quarter</SectionLabel>
                <div className="grid grid-cols-4 gap-1.5">
                  {([1, 2, 3, 4] as const).map((q) => (
                    <PeriodOption
                      key={q}
                      selected={draft.quarter === q}
                      onClick={() => setDraft((d) => ({ ...d, quarter: q }))}
                    >
                      Q{q}
                    </PeriodOption>
                  ))}
                </div>
              </>
            )}

            {draft.mode === 'half' && (
              <>
                <SectionLabel>Half</SectionLabel>
                <div className="grid grid-cols-2 gap-1.5">
                  {([1, 2] as const).map((h) => (
                    <PeriodOption
                      key={h}
                      selected={draft.half === h}
                      onClick={() => setDraft((d) => ({ ...d, half: h }))}
                    >
                      H{h}
                    </PeriodOption>
                  ))}
                </div>
              </>
            )}

            {draft.mode === 'custom' ? (
              <div className={showModes ? 'mt-[0.75rem]' : ''}>
                <RangeCalendar
                  months={2}
                  monthYearSelect
                  value={customRange}
                  onChange={(range) => {
                    setCustomRange(range)
                    // A manual calendar edit diverges from whatever named preset
                    // seeded the draft, so drop the preset label: Apply then emits
                    // a plain custom range (not one masquerading as "Last N
                    // months"), and the quick-select highlight deselects. Only
                    // real user edits fire onChange — the controlled `value`
                    // seeding (open / preset click) does not — so this never
                    // clears a label the user hasn't actually diverged from.
                    setDraft((d) => (d.label ? { ...d, label: undefined } : d))
                  }}
                />
              </div>
            ) : (
              <>
                <SectionLabel>Year</SectionLabel>
                <div className="grid grid-cols-3 gap-1.5">
                  {yearList.map((y) => (
                    <PeriodOption
                      key={y}
                      selected={draft.year === y}
                      onClick={() => setDraft((d) => ({ ...d, year: y }))}
                    >
                      {y}
                    </PeriodOption>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right: quick-select presets as a list (the date-range pattern). */}
          <FilterQuickSelect
            items={quickSelectItems.map((p) => ({
              label: p.label,
              selected: isPresetActive(p.value),
              onSelect: () => selectPreset(p.value),
            }))}
          />
        </div>

        <FilterActions
          onCancel={() => setOpen(false)}
          onApply={apply}
          applyDisabled={!draftComplete}
        />
      </PopoverContent>
    </Popover>
  )
}
